import React from "react";
import { LegalLayout, S, UL, Mail, Phone, BRAND, CancellationTable } from "@/components/Legal";

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" testid="privacy-page">
      <p>This Privacy Policy explains how {BRAND.name} ("SCHOKODALE", "we", "us") collects, uses and protects your personal information when you visit our website or buy from us. It is published in accordance with the Information Technology Act, 2000, the rules made under it, and the Digital Personal Data Protection Act, 2023. By using our website you agree to this policy.</p>
      <S h="1. Information we collect">
        <UL items={[
          "Contact details: your name, email address, mobile/WhatsApp number and delivery address.",
          "Order details: products purchased, gift messages, promo codes used and order history.",
          "Enquiry details: information you share in gifting, corporate or customisation enquiries, including logos or photos you upload.",
          "Catalogue requests: name, WhatsApp number and city when you download our catalogue.",
          "Technical data: browser type, device and pages visited, used only to keep the website secure and working well.",
        ]} />
        <p>We do not collect or store your card, UPI or net-banking credentials. All payments are processed directly by Razorpay, a PCI-DSS compliant payment gateway.</p>
      </S>
      <S h="2. How we use your information">
        <UL items={[
          "To process, prepare, deliver and support your orders.",
          "To send order confirmations, sign-in codes and order status updates by email, SMS or WhatsApp.",
          "To respond to your enquiries and prepare quotes for bulk, corporate or customised orders.",
          "To share offers and new collections, only where you have contacted us or requested our catalogue. You may opt out at any time.",
          "To prevent fraud, misuse of promo codes and to meet legal and tax obligations.",
        ]} />
      </S>
      <S h="3. Sharing of information">
        <p>We never sell or rent your personal data. We share only what is necessary with trusted service providers who help us run our business — payment processing (Razorpay), courier and delivery partners, email/SMS/WhatsApp messaging services and secure cloud hosting — and with government authorities when required by law.</p>
      </S>
      <S h="4. Data security & retention">
        <p>Your data is stored on secure servers with restricted access. Sign-in uses one-time passcodes, so we never store passwords. We retain order and invoice records for as long as required under Indian tax laws, and other data only for as long as needed for the purposes above.</p>
      </S>
      <S h="5. Your rights">
        <p>You may request access to, correction of, or deletion of your personal data (subject to legal record-keeping requirements), or withdraw consent for marketing, by writing to <Mail />.</p>
      </S>
      <S h="6. Cookies">
        <p>We use essential browser storage to keep your cart, wishlist and sign-in session working. We do not use third-party advertising cookies.</p>
      </S>
      <S h="7. Children">
        <p>Our website is intended for users aged 18 and above. Orders for children should be placed by a parent or guardian.</p>
      </S>
      <S h="8. Grievance Officer">
        <p>For any privacy concern or complaint, contact our Grievance Officer at <Mail /> or <Phone />. We will acknowledge your complaint within 48 hours and aim to resolve it within 30 days.</p>
      </S>
      <S h="9. Changes to this policy">
        <p>We may update this policy from time to time. The latest version will always be available on this page.</p>
      </S>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms & Conditions" testid="terms-page">
      <p>These Terms & Conditions govern your use of the SCHOKODALE website and all purchases made on it. By placing an order you agree to these terms, together with our Privacy Policy, Refund & Cancellation Policy and Shipping & Delivery Policy.</p>
      <S h="1. About us">
        <p>This website is operated by {BRAND.name}, a seller of premium chocolates and confectionery in India. Contact: <Mail /> · <Phone />.</p>
      </S>
      <S h="2. Products">
        <UL items={[
          "Our chocolates are freshly prepared. Product images are for presentation purposes; actual shape, colour, decoration and packaging may vary slightly.",
          "Quantity and price vary according to the selected pack size. All weights and piece counts are as stated on the product page.",
          "Ingredients are listed on each product page. Our products may contain or come into contact with milk, nuts, gluten, soy and other allergens. Please check ingredients carefully if you have any allergy; we cannot guarantee an allergen-free environment.",
          "Storage: keep in a cool, dry place at 3–5°C, away from sunlight, heat and moisture. Shelf life: 15 days from date of dispatch. We are not responsible for quality issues caused by improper storage after delivery.",
        ]} />
      </S>
      <S h="3. Pricing & payment">
        <UL items={[
          "All prices are in Indian Rupees (₹) and are the Maximum Retail Price (MRP), inclusive of applicable taxes unless stated otherwise.",
          "We accept prepaid orders only through Razorpay (UPI, credit/debit cards, net banking and wallets). Cash on Delivery (COD) is not available.",
          "An order is confirmed only after successful payment. We reserve the right to cancel and fully refund any order affected by a pricing or technical error, stock unavailability or suspected fraud.",
          "Delivery is free on orders of ₹3,500 and above (after discounts); a delivery charge of ₹150 applies to smaller orders.",
        ]} />
      </S>
      <S h="4. Promo codes & offers">
        <UL items={[
          "Promo codes are subject to their individual terms — validity dates, minimum order value, maximum discount, usage limits and eligible products or categories.",
          "Only one promo code can be applied per order. Promo codes cannot be combined with products that are already discounted.",
          "Promo codes have no cash value and cannot be exchanged or transferred. We may withdraw or modify any offer at any time and cancel orders that misuse offers.",
        ]} />
      </S>
      <S h="5. Customised & bulk orders">
        <p>Logo chocolates, photo chocolates and branded packaging are made to order for bulk quantities on a quote basis. You confirm that you own or have permission to use any logo, photograph, name or artwork you submit, and you agree to indemnify us against any claim arising from it. Final design, quantity, price and delivery date are confirmed in writing before production; once production begins, customised orders cannot be cancelled.</p>
      </S>
      <S h="6. Cancellations, refunds & delivery">
        <p>Cancellations are governed by our Refund & Cancellation Policy (no refunds once delivered; time-based cancellation charges apply). Deliveries are governed by our Shipping & Delivery Policy.</p>
        <CancellationTable />
      </S>
      <S h="7. Use of the website">
        <p>You agree not to misuse the website, attempt unauthorised access, or use it for any unlawful purpose. All content — including the SCHOKODALE name, logo, photographs, designs and text — is our property and may not be copied or used without our written permission.</p>
      </S>
      <S h="8. Limitation of liability">
        <p>To the maximum extent permitted by law, our total liability for any claim relating to an order is limited to the amount paid for that order. We are not liable for delays or failures caused by events beyond our reasonable control, including courier delays, weather, strikes or natural events.</p>
      </S>
      <S h="9. Governing law">
        <p>These terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the courts having jurisdiction over SCHOKODALE's principal place of business.</p>
      </S>
      <S h="10. Food safety & regulatory information">
        <p data-testid="fssai-details">FSSAI Registration No.: <span className="text-[#FDFBF7] font-semibold">{BRAND.fssai}</span></p>
        <p>Grievances may be raised with our Grievance Officer at <Mail /> or <Phone />, in accordance with the Consumer Protection (E-Commerce) Rules, 2020.</p>
      </S>
    </LegalLayout>
  );
}

export function RefundCancellation() {
  return (
    <LegalLayout title="Refund & Cancellation Policy" testid="refund-page">
      <p>Because our chocolates are perishable, freshly prepared food products, we follow the policy below. Please read it carefully before placing your order.</p>
      <S h="1. No refunds after delivery">
        <p>We do not offer refunds or returns on chocolates once they have been delivered, as food products cannot be resold for hygiene and safety reasons.</p>
      </S>
      <S h="2. Order cancellation">
        <p>You may cancel a prepaid order before it is dispatched. Cancellation charges depend on how long after placing the order you cancel:</p>
        <CancellationTable />
        <UL items={[
          "To cancel, email us at schokodale@gmail.com or WhatsApp +91 76229 27835 with your Order ID. The time we receive your request is treated as the cancellation time.",
          "Orders cannot be cancelled once dispatched.",
          "Customised, personalised and bulk orders cannot be cancelled once production has begun.",
          "Promo-code discounts and gift-wrap charges are not refundable on cancellation; charges are calculated on the amount actually paid.",
        ]} />
      </S>
      <S h="3. Damaged, wrong or missing items">
        <p>If your order arrives damaged, melted in transit, incorrect or incomplete, please contact us within 24 hours of delivery with your Order ID, clear photos and, preferably, an unboxing video. After verification we will send a free replacement of the affected items. Replacements are offered in place of refunds.</p>
      </S>
      <S h="4. Cancellations by SCHOKODALE">
        <p>If we have to cancel your order (for example, due to stock unavailability, a non-serviceable pincode or a payment issue), you will receive a 100% refund.</p>
      </S>
      <S h="5. How amounts are returned">
        <p>Any eligible amount is returned to your original payment method via Razorpay within 5–7 business days of the cancellation being confirmed. Cash on Delivery is not offered.</p>
      </S>
      <p>Questions? Write to <Mail /> or call <Phone />.</p>
    </LegalLayout>
  );
}

export function ShippingDelivery() {
  return (
    <LegalLayout title="Shipping & Delivery Policy" testid="shipping-page">
      <S h="1. Where we deliver">
        <p>We deliver across India through trusted courier partners. If a pincode is not serviceable, we will contact you and fully refund the order.</p>
      </S>
      <S h="2. Delivery charges">
        <UL items={["Free delivery on orders of ₹3,500 and above (order value after discounts).", "A flat delivery charge of ₹150 applies to orders below ₹3,500.", "All orders are prepaid; Cash on Delivery (COD) is not available."]} />
      </S>
      <S h="3. Dispatch & delivery timelines">
        <UL items={[
          "Orders are freshly prepared and usually dispatched within 1–3 business days of payment confirmation.",
          "Delivery typically takes 2–7 business days after dispatch, depending on your location.",
          "Customised and bulk orders follow the timeline agreed in your confirmed quote.",
          "During festive seasons, extreme weather or other events beyond our control, deliveries may take longer. We will keep you informed.",
        ]} />
      </S>
      <S h="4. Packaging & weather">
        <p>Chocolates are temperature-sensitive. We pack every order with care in protective packaging. In very hot weather we may hold dispatch briefly to protect quality, and will inform you if this affects your order. Please refrigerate your chocolates (3–5°C) soon after delivery.</p>
      </S>
      <S h="5. Order updates">
        <p>You will receive order confirmation and status updates by email (and SMS/WhatsApp where available). You can also view your orders in My Account.</p>
      </S>
      <S h="6. Receiving your order">
        <UL items={[
          "Please make sure your address, pincode and phone number are correct. We are not responsible for delays or loss caused by incorrect details.",
          "Please accept the parcel promptly. Chocolates left uncollected or returned due to an unavailable recipient cannot be refunded; re-delivery may be charged.",
          "If the outer package looks damaged or tampered with, please note it with the courier and contact us within 24 hours with photos.",
        ]} />
      </S>
      <p>Need help with a delivery? Email <Mail /> or WhatsApp <Phone />.</p>
    </LegalLayout>
  );
}
